import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bookmark, BookmarkPlus, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const MAX = 5;

export const MusicPresets = ({ cat, music, setCfg }) => {
  const { t, lang } = useI18n();
  const [list, setList] = useState([]);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  useEffect(() => { api.get("/music/presets").then((r) => setList(r.data)).catch(() => {}); }, []);
  const track = (id) => (cat.music || []).find((m) => m.id === id);
  const apply = (p) => {
    if (!track(p.music.id)) return toast.error(t("mp.missing"));
    setCfg((x) => ({ ...x, music: { ...p.music } }));
    toast.success(t("mp.applied").replace("{n}", p.name));
  };
  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/music/presets", { name: name.trim(), music });
      setList((l) => [...l, data]);
      setNaming(false);
      setName("");
      toast.success(t("mp.saved"));
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const del = async (p) => {
    try { await api.delete(`/music/presets/${p.id}`); setList((l) => l.filter((x) => x.id !== p.id)); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="rounded-lg border border-[#a746ff]/30 bg-[#a746ff]/[0.06] p-2.5 space-y-2" data-testid="music-presets">
      <div className="flex items-center gap-2 text-[11px] font-semibold text-[#c79bff]">
        <Bookmark className="w-3.5 h-3.5" />{t("mp.title")} <span className="text-slate-500 font-mono" data-testid="music-presets-count">{list.length}/{MAX}</span>
        {music && !naming && list.length < MAX && (
          <button type="button" onClick={() => { setNaming(true); setName(track(music.id)?.[lang === "fr" ? "fr" : "en"]?.slice(0, 30) || ""); }} data-testid="music-preset-save-btn"
            className="ml-auto inline-flex items-center gap-1 rounded-full cx-metal px-2.5 py-1 text-[10px] text-white"><BookmarkPlus className="w-3 h-3" />{t("mp.save")}</button>
        )}
      </div>
      {naming && (
        <form onSubmit={save} className="flex gap-1.5" data-testid="music-preset-form">
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={30} autoFocus placeholder={t("mp.name_ph")} data-testid="music-preset-name-input"
            className="flex-1 min-w-0 rounded-md bg-black/40 border border-white/15 px-2 py-1 text-xs text-white outline-none focus:border-[#a746ff]" />
          <button type="submit" disabled={!name.trim()} data-testid="music-preset-confirm-btn" className="rounded-md bg-[#a746ff] px-2.5 text-[11px] font-semibold text-white disabled:opacity-40">OK</button>
          <button type="button" onClick={() => setNaming(false)} data-testid="music-preset-cancel-btn" className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </form>
      )}
      {list.length ? (
        <div className="flex flex-wrap gap-1.5">
          {list.map((p) => (
            <span key={p.id} className={`inline-flex items-center rounded-full border text-[11px] ${music?.id === p.music.id ? "border-[#a746ff] bg-[#a746ff]/20" : "border-white/15 bg-black/30"}`} data-testid={`music-preset-${p.id}`}>
              <button type="button" onClick={() => apply(p)} data-testid={`music-preset-apply-${p.id}`} className="pl-2.5 pr-1 py-1 text-white hover:text-[#c79bff]" title={track(p.music.id)?.[lang === "fr" ? "fr" : "en"]}>{p.name}</button>
              <button type="button" onClick={() => del(p)} data-testid={`music-preset-delete-${p.id}`} aria-label="delete" className="pr-2 text-slate-500 hover:text-[#ff3b5c]"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      ) : (
        <p className="text-[10px] text-slate-500" data-testid="music-presets-empty">{t("mp.empty")}</p>
      )}
    </div>
  );
};
